import { AssignIcbtModal } from "components/icbt/assignIcbtModal";
import { useCallback } from "react";
import { useMindlerApi } from "api/apiv2";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useFlag } from "@mindlercare/unleash-client";
import { usePsychologistUser } from "hooks";
import { useGetIcbtSequentialUnlock } from "api/useIcbtQueries";
import {
  getFeedbackLockReason,
  getIsFeedbackLocked,
  getIsRequestOwner,
  getRequestingPsychologistId,
} from "../../useIcbtTableData";
import { FeedbackModal } from "./FeedbackModal";
import { getAssessment } from "../../useIcbtTableData/utils";
export type ModalState = {
  programId: string;
  treatmentId: string;
  userId?: number;
  icbtRequestId: string;
  icbtFeedbackDate: string;
  icbtFeedbackDateIgnored: boolean;
  patientName: string;
  feedbackModalOpen?: boolean;
  assignIcbtModalOpen?: boolean;
};
interface Props {
  modalState: ModalState;
  setModalState: (data: ModalState) => void;
}
/**
 * Feedback modal with AssignIcbtModal
 *
 */
export const FeedbackModalStack = ({ modalState, setModalState }: Props) => {
  const {
    icbtRequestId,
    programId,
    treatmentId,
    userId: patientUserId,
    feedbackModalOpen,
    assignIcbtModalOpen,
    icbtFeedbackDate,
    icbtFeedbackDateIgnored,
  } = modalState;

  const feedbackLockEnabled = useFlag(
    "ICBT_LOCK_FEEDBACK_UNTIL_PATIENT_ACTIVE",
  );

  const apiv2 = useMindlerApi();
  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(patientUserId),
      },
      {
        enabled: !!patientUserId,
      },
    ),
  );

  const { data: icbtSequentialUnlock } = useGetIcbtSequentialUnlock(
    { userId: String(patientUserId) },
    { enabled: !!patientUserId && !!feedbackModalOpen },
  );

  const { psychologist } = usePsychologistUser();
  const isRequestOwner = getIsRequestOwner(
    icbtSequentialUnlock,
    icbtRequestId,
    psychologist?.psychologistId,
  );
  const requestingPsychologistId = getRequestingPsychologistId(
    icbtSequentialUnlock,
    programId,
    treatmentId,
    icbtRequestId,
  );

  const handleCloseFeedbackModal = useCallback(() => {
    setModalState({
      ...modalState,
      feedbackModalOpen: false,
      assignIcbtModalOpen: false,
    });
  }, [setModalState, modalState]);

  const handleOpenAssignIcbtModal = useCallback(() => {
    setModalState({
      ...modalState,
      feedbackModalOpen: false,
      assignIcbtModalOpen: true,
    });
  }, [setModalState, modalState]);

  const handleAssignIcbtModalClose = useCallback(() => {
    setModalState({
      ...modalState,
      assignIcbtModalOpen: false,
      feedbackModalOpen: true,
    });
  }, [setModalState, modalState]);

  return (
    <>
      {assignIcbtModalOpen && patient && (
        <AssignIcbtModal
          patient={patient}
          closeModal={handleAssignIcbtModalClose}
          onAssignment={handleAssignIcbtModalClose}
        />
      )}
      {patient?.userId && feedbackModalOpen && (
        <FeedbackModal
          handleCloseFeedbackModal={handleCloseFeedbackModal}
          programId={programId}
          treatmentId={treatmentId}
          open={!!feedbackModalOpen}
          userId={patient.userId}
          icbtRequestId={icbtRequestId}
          patientName={`${patient.firstName} ${patient.lastName}`}
          patientSsn={patient.nationalIdentity}
          setAssignIcbtModalIsOpen={handleOpenAssignIcbtModal}
          icbtFeedbackDate={icbtFeedbackDate}
          icbtFeedbackDateIgnored={icbtFeedbackDateIgnored}
          isFeedbackLocked={
            feedbackLockEnabled &&
            getIsFeedbackLocked(
              icbtSequentialUnlock,
              programId,
              treatmentId,
              icbtRequestId,
            )
          }
          feedbackLockReason={getFeedbackLockReason(
            icbtSequentialUnlock,
            programId,
            treatmentId,
            icbtRequestId,
          )}
          isRequestOwner={isRequestOwner}
          requestingPsychologistId={requestingPsychologistId}
          assessment={getAssessment(
            icbtSequentialUnlock,
            programId,
            treatmentId,
            icbtRequestId,
          )}
        />
      )}
    </>
  );
};
